// Types and helpers for the fleet-wide month view of owner statements.
//
// Mirrors the shape returned by /api/statements/getStatementMatrix. The row
// order, the section headings and the three groups are all deliberately the
// same as the month-end workbook that used to be assembled by hand, so a figure
// can be found in the same place on the screen as on the sheet.

export type StatementGroup = 'new_model' | 'active' | 'offboarded'

export type StatementMatrixPeriod = {
  period_start: string
  period_end: string
  period_label: string
  statement_count: number
  draft_count: number
  sendable_count: number
  payout_total_zar: number
}

export type StatementMatrixColumn = {
  statement_id: string
  owner_id: string
  owner_name: string
  owner_email: string
  vehicle_id: string
  plate: string
  vehicle_name: string
  operational_state: string
  lifecycle_status: string
  group_key: StatementGroup
  is_draft: boolean
  do_not_send: boolean
  issued_at: string | null
  has_source_errors: boolean
  net_zar: number
  payout_amount_zar: number
  closing_balance_zar: number
  retention_balance_zar: number
  // What was found afterwards and belongs to THIS month. net_zar stays exactly
  // what the owner's statement said.
  net_correction_zar: number
  net_actual_zar: number
  // How many months of this vehicle the column covers, and which.
  periods_covered: number
  first_period: string
  last_period: string
}

export type StatementMatrixRow = {
  line_code: string
  label: string
  description: string | null
  section: string
  sort_order: number
  is_subtotal: boolean
  is_performance: boolean
  is_money: boolean
  // "Payouts Due" is stored negative and read positive. The handler derives
  // this from the database's own display_value rather than hardcoding the line.
  display_abs: boolean
}

export type StatementMatrixCell = {
  v: number | null
  e?: true
  n?: string
  // Present only where the figure has been corrected since the statement went
  // out. `a` is what the statement said, `c` is the correction that belongs to
  // this month, `w` is why.
  a?: number
  c?: number
  w?: string
  // False when this vehicle carries no such line at all. The value is still a
  // zero; this is what tells an absent line from a genuine R0.00.
  p?: false
  // How a range was aggregated: sum, first, last or ratio. Absent on one month.
  m?: string
  // Live only: where a figure came from when it is not the live calculation.
  s?: string
}

// 'actual' is what the month actually was, once everything found afterwards is
// applied to the month it happened in. 'as_issued' is what the owner's
// statement said on the day it was sent. The document settles corrections
// forward; this screen does not have to.
//
// 'live' is the month recomputed from the database on every request, without
// statement adjustments, balances or payouts. It is its own tab on Owner
// Statements rather than an option in the Figures menu, because it answers a
// different question: not "what did we tell the owner" but "what does the
// database say happened".
export type StatementBasis = 'actual' | 'as_issued' | 'live'

export type StatementRange = {
  from: string
  to: string
  periods_covered: number
  label: string
}

export type StatementMatrix = {
  basis: StatementBasis
  computed_at: string
  live_source_from: string | null
  range: StatementRange | null
  owners: { owner_id: string; owner_name: string }[]
  vehicles: { vehicle_id: string; plate: string }[]
  periods: StatementMatrixPeriod[]
  period: StatementMatrixPeriod | null
  columns: StatementMatrixColumn[]
  rows: StatementMatrixRow[]
  cells: Record<string, Record<string, StatementMatrixCell>>
}

export const EMPTY_MATRIX: StatementMatrix = {
  basis: 'actual',
  computed_at: '',
  live_source_from: null,
  range: null,
  owners: [],
  vehicles: [],
  periods: [],
  period: null,
  columns: [],
  rows: [],
  cells: {},
}

// The workbook's headings, verbatim, so the screen and the export read the
// same. `balance` legitimately appears twice: once for the opening balance and
// again for the closing balance, which is how the statement is laid out.
const SECTION_HEADINGS: Record<string, string> = {
  balance: 'BALANCE',
  earnings: 'EARNINGS',
  cost_of_sales: 'COST OF SALES',
  summary: 'SUMMARY',
  operational_costs: 'OPERATIONAL COSTS',
  retention: 'RESULT / RETENTION',
  payments: 'PAYOUT',
  performance: 'PERFORMANCE',
}

export function sectionHeading(code: string): string {
  return SECTION_HEADINGS[code] ?? code.replace(/_/g, ' ').toUpperCase()
}

export const GROUP_LABELS: Record<StatementGroup, string> = {
  new_model: 'New model vehicles',
  active: 'Old model active vehicles',
  offboarded: 'Offboarded vehicles',
}

// Sheet order in the workbook.
export const GROUP_ORDER: StatementGroup[] = ['new_model', 'active', 'offboarded']

// A ratio cannot be added up. Earnings per shift across ten vehicles is not the
// sum of ten per-shift figures, so those rows are never summed.
//
// They are not left blank either. A fleet total for a ratio exists, it is just
// not a sum: it is the fleet numerator over the fleet denominator, which is the
// same arithmetic the single-vehicle figure uses one level down. DERIVED_TOTALS
// says which two rows make each one, and rowTotal divides them.
export function isSummable(row: StatementMatrixRow): boolean {
  return !(row.is_performance && row.is_money)
}

// The three ratio rows, and the two summable rows each is built from. The
// denominators come off the same matrix, so a filtered view of the fleet gives
// the ratio for exactly the vehicles on screen.
const DERIVED_TOTALS: Record<string, { numerator: string; denominator: string }> = {
  earnings_per_shift: { numerator: 'total_earnings', denominator: 'shifts' },
  earnings_per_km: { numerator: 'total_earnings', denominator: 'kms' },
  net_per_km: { numerator: 'net', denominator: 'kms' },
}

/** Sum one line code across the columns on screen. Null when no column has it. */
function sumLine(
  lineCode: string,
  columns: StatementMatrixColumn[],
  cells: StatementMatrix['cells'],
): number | null {
  let total = 0
  let seen = false
  for (const column of columns) {
    const cell = cells[column.statement_id]?.[lineCode]
    if (!cell || cell.v === null || cell.e) continue
    total += cell.v
    seen = true
  }
  return seen ? total : null
}

/**
 * Whether the Total column has a number for this row. A ratio has one even
 * though it cannot be summed, because DERIVED_TOTALS knows how to build it.
 */
export function hasFleetTotal(row: StatementMatrixRow): boolean {
  return isSummable(row) || row.line_code in DERIVED_TOTALS
}

/** The number to show for a cell or a total, after the display convention. */
export function displayValue(row: StatementMatrixRow, value: number): number {
  return row.display_abs ? Math.abs(value) : value
}

export function rowTotal(
  row: StatementMatrixRow,
  columns: StatementMatrixColumn[],
  cells: StatementMatrix['cells'],
): number | null {
  const derived = DERIVED_TOTALS[row.line_code]
  if (derived) {
    const numerator = sumLine(derived.numerator, columns, cells)
    const denominator = sumLine(derived.denominator, columns, cells)
    // A zero denominator is a fleet that ran no shifts or no kilometres. There
    // is no ratio to show, and showing zero would read as a fleet that earned
    // nothing per kilometre rather than one that drove none.
    if (numerator === null || denominator === null || denominator === 0) return null
    return numerator / denominator
  }
  if (!isSummable(row)) return null
  let total = 0
  let seen = false
  for (const column of columns) {
    const cell = cells[column.statement_id]?.[row.line_code]
    if (!cell || cell.v === null || cell.e) continue
    total += cell.v
    seen = true
  }
  return seen ? total : null
}

/** A short label for the month, e.g. "August 2026". Drops any DRAFT suffix. */
export function periodShortLabel(period: StatementMatrixPeriod): string {
  const label = period.period_label || period.period_start
  return label.replace(/\s*\(.*\)\s*$/, '').trim() || period.period_start
}

/** A filename-safe stem, e.g. "Fleetly_Statements_2026-08". */
export function periodFileStem(period: StatementMatrixPeriod): string {
  return `Fleetly_Statements_${period.period_start.slice(0, 7)}`
}
